import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table,
  Button,
  Drawer,
  Form,
  Input,
  Checkbox,
  Space,
  message,
  Tag,
  Popconfirm,
  Typography,
  Tabs,
  Alert,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { TableRowActions } from '../components/TableRowActions';
import {
  supabase,
  PORTAL_PERMISSIONS,
  usePortalPermissions,
  PermissionGate,
} from '@ubus/shared';
import AdminListPage from '../components/AdminListPage';
import { useAdminTableStability } from '../hooks/useAdminTableStability';
import { useTranslation } from 'react-i18next';
import { formatAdminDbError } from '../utils/formatAdminDbError';

const { Text } = Typography;

const FLEET_TEMPLATE_CODES = ['fleet_manager', 'fleet_dispatcher', 'fleet_read_only'] as const;
type FleetTemplateCode = (typeof FLEET_TEMPLATE_CODES)[number];

function isFleetTemplateCode(code: string): code is FleetTemplateCode {
  return (FLEET_TEMPLATE_CODES as readonly string[]).includes(code);
}

interface PortalRole {
  id: string;
  code: string;
  name: string;
  description: string | null;
  is_system: boolean;
  updated_at: string;
}

function groupPermissionsByPage(codes: readonly string[]) {
  const map = new Map<string, string[]>();
  for (const code of codes) {
    const page = code.split('.')[0] ?? code;
    const list = map.get(page) ?? [];
    list.push(code);
    map.set(page, list);
  }
  return [...map.entries()];
}

function roleDisplayName(
  role: PortalRole,
  t: (key: string, options?: { defaultValue?: string }) => string
): string {
  if (!role.is_system) return role.name;
  return t(`rbac.systemRoles.${role.code}.name`, { defaultValue: role.name });
}

function RolesPage() {
  const { t, i18n } = useTranslation();
  const { hasPermission, refresh: refreshAccess } = usePortalPermissions();
  const canManage = hasPermission('system.roles.manage');
  const [roles, setRoles] = useState<PortalRole[]>([]);
  const [rolePerms, setRolePerms] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<PortalRole | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<{
    code: string;
    name: string;
    description?: string;
    permissions: string[];
  }>();
  const { pagination, onTableChange, rowClassName, afterMutation } = useAdminTableStability();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: roleRows, error: roleErr }, { data: rpRows, error: rpErr }] =
        await Promise.all([
          supabase.from('portal_roles').select('*').order('name'),
          supabase.from('portal_role_permissions').select('role_id, permission_code'),
        ]);
      if (roleErr) throw roleErr;
      if (rpErr) throw rpErr;
      setRoles((roleRows as PortalRole[]) ?? []);
      const map: Record<string, string[]> = {};
      for (const row of rpRows ?? []) {
        const rid = (row as { role_id: string }).role_id;
        const code = (row as { permission_code: string }).permission_code;
        map[rid] = map[rid] ? [...map[rid], code] : [code];
      }
      setRolePerms(map);
    } catch (e) {
      message.error(e instanceof Error ? e.message : t('rbac.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({
      code: '',
      name: '',
      description: '',
      permissions: [],
    });
    setDrawerOpen(true);
  };

  const openEdit = (role: PortalRole) => {
    setEditing(role);
    form.setFieldsValue({
      code: role.code,
      name: role.is_system
        ? t(`rbac.systemRoles.${role.code}.name`, { defaultValue: role.name })
        : role.name,
      description: role.is_system
        ? t(`rbac.systemRoles.${role.code}.description`, {
            defaultValue: role.description ?? '',
          })
        : (role.description ?? ''),
      permissions: rolePerms[role.id] ?? [],
    });
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      let mutatedId: string | null = null;
      if (editing) {
        mutatedId = editing.id;
        if (!editing.is_system) {
          const { error: updErr } = await supabase.rpc('update_portal_role', {
            p_role_id: editing.id,
            p_name: values.name.trim(),
            p_description: values.description?.trim() || null,
          });
          if (updErr) throw updErr;
        }

        const { error } = await supabase.rpc('set_portal_role_permissions', {
          p_role_id: editing.id,
          p_codes: values.permissions,
        });
        if (error) throw error;
      } else {
        const { data: created, error } = await supabase.rpc('create_portal_role', {
          p_code: values.code.trim(),
          p_name: values.name.trim(),
          p_description: values.description?.trim() || null,
          p_codes: values.permissions,
        });
        if (error) throw error;
        mutatedId = (created as PortalRole | null)?.id ?? null;
      }
      message.success(t('rbac.saved'));
      setDrawerOpen(false);
      await afterMutation(mutatedId, load);
      await refreshAccess();
    } catch (e) {
      message.error(e instanceof Error ? e.message : t('rbac.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role: PortalRole) => {
    try {
      const { error } = await supabase.rpc('delete_portal_role', {
        p_role_id: role.id,
      });
      if (error) throw error;
      message.success(t('rbac.deleted'));
      await load();
    } catch (e) {
      message.error(formatAdminDbError(e, i18n.language, 'delete'));
    }
  };

  const permGroups = useMemo(
    () => groupPermissionsByPage(PORTAL_PERMISSIONS),
    []
  );

  const buildColumns = (allowDelete: boolean) => [
    {
      title: t('rbac.roleName'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      fixed: 'left' as const,
      ellipsis: true,
      render: (_: string, r: PortalRole) => roleDisplayName(r, t),
    },
    {
      title: t('rbac.roleCode'),
      dataIndex: 'code',
      key: 'code',
      width: 140,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: t('rbac.permissionsCount'),
      key: 'perms',
      width: 120,
      render: (_: unknown, r: PortalRole) => rolePerms[r.id]?.length ?? 0,
    },
    {
      title: t('rbac.system'),
      dataIndex: 'is_system',
      key: 'is_system',
      width: 100,
      render: (v: boolean) =>
        v ? <Tag color="blue">{t('rbac.systemYes')}</Tag> : <Tag>{t('rbac.systemNo')}</Tag>,
    },
    {
      title: t('rbac.actions'),
      key: 'actions',
      width: 180,
      fixed: 'right' as const,
      render: (_: unknown, record: PortalRole) => (
        <PermissionGate permission="system.roles.manage">
          <TableRowActions>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(record)}
            >
              {t('rbac.edit')}
            </Button>
            {allowDelete && !record.is_system ? (
              <Popconfirm
                title={t('rbac.deleteConfirm')}
                okText={t('common.delete')}
                cancelText={t('common.cancel')}
                onConfirm={() => void handleDelete(record)}
              >
                <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                  {t('rbac.delete')}
                </Button>
              </Popconfirm>
            ) : null}
          </TableRowActions>
        </PermissionGate>
      ),
    },
  ];

  const platformRoles = useMemo(
    () => roles.filter((r) => !r.code.startsWith('fleet_')),
    [roles]
  );
  const fleetTemplateRoles = useMemo(
    () => roles.filter((r) => isFleetTemplateCode(r.code)),
    [roles]
  );

  return (
    <>
      <AdminListPage
        title={t('rbac.rolesTitle')}
        subtitle={t('rbac.rolesSubtitle')}
        resultCount={roles.length}
        extra={
          canManage ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              {t('rbac.newRole')}
            </Button>
          ) : null
        }
      >
        <Tabs
          defaultActiveKey="platform"
          items={[
            {
              key: 'platform',
              label: t('rbac.tabs.platform'),
              children: (
                <Table
                  rowKey="id"
                  loading={loading}
                  dataSource={platformRoles}
                  columns={buildColumns(true)}
                  pagination={pagination}
                  onChange={onTableChange}
                  rowClassName={rowClassName}
                  scroll={{ x: 800 }}
                />
              ),
            },
            {
              key: 'fleetTemplates',
              label: t('rbac.tabs.fleetTemplates'),
              children: (
                <>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={t('rbac.fleetTemplateLocked')}
                  />
                  <Table
                    rowKey="id"
                    loading={loading}
                    dataSource={fleetTemplateRoles}
                    columns={buildColumns(false)}
                    pagination={pagination}
                    onChange={onTableChange}
                    rowClassName={rowClassName}
                    scroll={{ x: 800 }}
                  />
                </>
              ),
            },
          ]}
        />
      </AdminListPage>

      <Drawer
        title={editing ? t('rbac.editRole') : t('rbac.newRole')}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={560}
        destroyOnClose
        extra={
          <Button type="primary" loading={saving} onClick={() => void handleSave()}>
            {t('rbac.save')}
          </Button>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="code"
            label={t('rbac.roleCode')}
            rules={[{ required: true, message: t('rbac.codeRequired') }]}
          >
            <Input disabled={!!editing} placeholder={t('rbac.codePlaceholder')} />
          </Form.Item>
          <Form.Item
            name="name"
            label={t('rbac.roleName')}
            rules={[{ required: true, message: t('rbac.nameRequired') }]}
          >
            <Input disabled={!!editing?.is_system} />
          </Form.Item>
          <Form.Item name="description" label={t('rbac.description')}>
            <Input.TextArea rows={2} disabled={!!editing?.is_system} />
          </Form.Item>
          <Form.Item
            name="permissions"
            label={t('rbac.permissionMatrix')}
            rules={[
              {
                required: true,
                type: 'array',
                min: 1,
                message: t('rbac.permissionsRequired'),
              },
            ]}
          >
            <Checkbox.Group style={{ width: '100%' }}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {permGroups.map(([page, codes]) => (
                  <div key={page}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      {t(`rbac.page.${page}`, { defaultValue: page })}
                    </Text>
                    <Space wrap>
                      {codes.map((code) => (
                        <Checkbox key={code} value={code}>
                          {t(`rbac.perm.${code}`, {
                            defaultValue: code.split('.').slice(1).join('.') || code,
                          })}
                        </Checkbox>
                      ))}
                    </Space>
                  </div>
                ))}
              </Space>
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
}

export default RolesPage;
